// Platform console -> Tenant detail -> AI plan & usage. G4 of
// docs/HMS_V4_AI_PROVIDER_MANAGEMENT.md §7.2/§7.4/§7.5/§9.4's "Platform
// console → Tenant detail → AI" row: per-tenant provider project mapping,
// allowance/markup/budgets/warning/hard-cap, usage/cost/charge (margin is
// provider_cost vs charge_inr, both shown here — the hospital-facing Usage
// tab deliberately never sees provider_cost, see admin-ai-usage.js), and
// reconciliation status.
import { useEffect, useState } from 'react';
import { platformApi } from '../lib/platformApi/client';

interface TenantAiPanelProps {
  tenantId: number;
  tenantSlug: string;
  tenantName: string;
  onBack: () => void;
}

interface Plan {
  tenant_id: number;
  enabled_document_providers: string[];
  enabled_voice_providers: string[];
  monthly_allowance_inr: number | null;
  provider_budgets_inr: Record<string, number>;
  warn_pct: number;
  hard_cap_inr: number | null;
  overage_allowed: boolean;
  markup_pct: number;
  requests_per_minute: number | null;
  max_concurrent_generations: number | null;
  updated_by: string | null;
  updated_at: string | null;
}

interface UsageRow {
  usage_date: string;
  provider: string;
  feature: string;
  calls: number;
  provider_cost: number;
  cost_currency: string | null;
  charge_inr: number | null;
}

interface OpenAiAccount {
  external_account_id: string;
  key_hint: string | null;
  provider_hard_cap_usd: number | null;
  setup_status: string;
  setup_message: string | null;
  updated_at: string;
}

interface ReconciliationRow {
  provider: string;
  usage_date: string;
  provider_reported_cost: number | null;
  hms_metered_cost: number | null;
  variance_pct: number | null;
  fetched_at: string;
}

const fieldStyle: React.CSSProperties = { width: '100%' };
const label: React.CSSProperties = { fontSize: 11, color: '#6B7280', marginBottom: 4, display: 'block' };
const grid2: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 };

export default function TenantAiPanel({ tenantId, tenantSlug, tenantName, onBack }: TenantAiPanelProps) {
  const [plan, setPlan] = useState<Plan | null>(null);
  const [edit, setEdit] = useState<Partial<Plan>>({});
  const [budgetsText, setBudgetsText] = useState('{}');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [usage, setUsage] = useState<{ rows: UsageRow[]; total_charge_inr: number } | null>(null);
  const [openaiAccount, setOpenaiAccount] = useState<OpenAiAccount | null>(null);
  const [provisioning, setProvisioning] = useState(false);
  const [reconciliation, setReconciliation] = useState<ReconciliationRow[]>([]);

  const refresh = () => {
    platformApi.get<{ plan: Plan }>(`/ai/tenants/${tenantId}/plan`).then(({ plan }) => {
      setPlan(plan);
      setEdit(plan);
      setBudgetsText(JSON.stringify(plan.provider_budgets_inr, null, 2));
    }).catch(e => setError(e instanceof Error ? e.message : String(e)));
    platformApi.get<{ rows: UsageRow[]; total_charge_inr: number }>(`/ai/tenants/${tenantId}/usage`).then(setUsage).catch(() => {});
    platformApi.get<{ account: OpenAiAccount | null }>(`/ai/tenants/${tenantId}/openai-account`).then(d => setOpenaiAccount(d.account)).catch(() => {});
    platformApi.get<{ rows: ReconciliationRow[] }>(`/ai/tenants/${tenantId}/reconciliation`).then(d => setReconciliation(d.rows)).catch(() => {});
  };

  useEffect(refresh, [tenantId]);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      let providerBudgets: Record<string, number> = {};
      try { providerBudgets = JSON.parse(budgetsText || '{}'); } catch { throw new Error('Provider budgets must be valid JSON, e.g. {"openai": 7000}'); }
      await platformApi.patch(`/ai/tenants/${tenantId}/plan`, {
        monthly_allowance_inr: edit.monthly_allowance_inr === undefined || edit.monthly_allowance_inr === null ? null : Number(edit.monthly_allowance_inr),
        markup_pct: edit.markup_pct != null ? Number(edit.markup_pct) : 0,
        warn_pct: edit.warn_pct != null ? Number(edit.warn_pct) : 80,
        hard_cap_inr: edit.hard_cap_inr === undefined || edit.hard_cap_inr === null || (edit.hard_cap_inr as unknown) === '' ? null : Number(edit.hard_cap_inr),
        overage_allowed: !!edit.overage_allowed,
        requests_per_minute: edit.requests_per_minute === undefined || edit.requests_per_minute === null || (edit.requests_per_minute as unknown) === '' ? null : Number(edit.requests_per_minute),
        max_concurrent_generations: edit.max_concurrent_generations === undefined || edit.max_concurrent_generations === null || (edit.max_concurrent_generations as unknown) === '' ? null : Number(edit.max_concurrent_generations),
        provider_budgets_inr: providerBudgets,
      });
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const provisionOpenAi = async () => {
    setProvisioning(true);
    setError('');
    try {
      await platformApi.post(`/ai/tenants/${tenantId}/provision-openai`, {});
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setProvisioning(false);
    }
  };

  if (!plan) {
    return (
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 20px' }}>
        {error ? <div style={{ color: '#991B1B', fontSize: 13 }}>{error}</div> : <div style={{ color: '#9CA3AF', fontSize: 13 }}>Loading…</div>}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>AI plan &amp; usage</div>
          <div style={{ fontSize: 12, color: '#6B7280' }}>{tenantName} ({tenantSlug})</div>
        </div>
        <button onClick={onBack} style={{ background: 'var(--bg)', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}>← Back</button>
      </div>

      {error && (
        <div style={{ padding: '8px 12px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, color: '#991B1B', fontSize: 13, marginBottom: 14 }}>{error}</div>
      )}

      <div className="card" style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Allowance &amp; markup (D27)</div>
        <div style={grid2}>
          <div>
            <label style={label}>Monthly allowance (₹)</label>
            <input style={fieldStyle} type="number" value={edit.monthly_allowance_inr ?? ''} onChange={e => setEdit(prev => ({ ...prev, monthly_allowance_inr: e.target.value === '' ? null : Number(e.target.value) }))} placeholder="e.g. 10000" />
          </div>
          <div>
            <label style={label}>Markup % (applied on top of raw provider cost)</label>
            <input style={fieldStyle} type="number" value={edit.markup_pct ?? 0} onChange={e => setEdit(prev => ({ ...prev, markup_pct: Number(e.target.value) }))} />
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Budgets, warnings &amp; caps (D25, D32)</div>
        <div style={grid2}>
          <div>
            <label style={label}>Warning threshold %</label>
            <input style={fieldStyle} type="number" value={edit.warn_pct ?? 80} onChange={e => setEdit(prev => ({ ...prev, warn_pct: Number(e.target.value) }))} />
          </div>
          <div>
            <label style={label}>Hard cap (₹) — empty = no hard cap</label>
            <input style={fieldStyle} type="number" value={edit.hard_cap_inr ?? ''} onChange={e => setEdit(prev => ({ ...prev, hard_cap_inr: e.target.value === '' ? null : Number(e.target.value) }))} placeholder="Leave empty — D32" />
          </div>
        </div>
        <label style={{ ...label, marginTop: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={edit.overage_allowed !== false} onChange={e => setEdit(prev => ({ ...prev, overage_allowed: e.target.checked }))} />
          Overage allowed (spend past the allowance continues, billed as overage)
        </label>
        <div style={{ marginTop: 12 }}>
          <label style={label}>Per-provider budgets (₹), JSON — e.g. {`{"openai": 7000, "sarvam": 3000}`}</label>
          <textarea style={{ ...fieldStyle, fontFamily: 'var(--mono)', fontSize: 12, minHeight: 80 }} value={budgetsText} onChange={e => setBudgetsText(e.target.value)} />
        </div>
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Rate limits</div>
        <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 12 }}>Noisy-neighbour guards, not billing controls — leave empty for unlimited.</div>
        <div style={grid2}>
          <div>
            <label style={label}>Requests per minute</label>
            <input style={fieldStyle} type="number" value={edit.requests_per_minute ?? ''} onChange={e => setEdit(prev => ({ ...prev, requests_per_minute: e.target.value === '' ? null : Number(e.target.value) }))} />
          </div>
          <div>
            <label style={label}>Max concurrent generations</label>
            <input style={fieldStyle} type="number" value={edit.max_concurrent_generations ?? ''} onChange={e => setEdit(prev => ({ ...prev, max_concurrent_generations: e.target.value === '' ? null : Number(e.target.value) }))} />
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <button className="btn-primary" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save plan'}</button>
        {plan.updated_at && <span style={{ marginLeft: 10, fontSize: 11, color: '#9CA3AF' }}>Last saved {new Date(plan.updated_at).toLocaleString()} by {plan.updated_by}</span>}
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>OpenAI project isolation (§7.2)</div>
        {openaiAccount ? (
          <div style={{ fontSize: 13 }}>
            <div>Status: <strong>{openaiAccount.setup_status}</strong></div>
            {openaiAccount.external_account_id && <div style={{ color: '#6B7280' }}>Project: {openaiAccount.external_account_id}</div>}
            {openaiAccount.key_hint && <div style={{ color: '#6B7280' }}>Key: {openaiAccount.key_hint}</div>}
            {openaiAccount.setup_message && <div style={{ color: '#991B1B', marginTop: 4 }}>{openaiAccount.setup_message}</div>}
          </div>
        ) : (
          <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 10 }}>No dedicated OpenAI project yet — this tenant's calls use the shared platform key.</div>
        )}
        {(!openaiAccount || openaiAccount.setup_status !== 'complete') && (
          <button className="btn-primary" disabled={provisioning} onClick={provisionOpenAi} style={{ marginTop: 10 }}>
            {provisioning ? 'Provisioning…' : 'Set up dedicated OpenAI project'}
          </button>
        )}
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>Usage &amp; charges (last 30 days)</div>
        {usage && usage.rows.length > 0 ? (
          <>
            <div style={{ fontSize: 13, marginBottom: 10 }}>Total charge: ₹{usage.total_charge_inr.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid #E5E7EB', color: '#6B7280' }}>
                    <th style={{ padding: '6px 8px' }}>Date</th>
                    <th style={{ padding: '6px 8px' }}>Provider</th>
                    <th style={{ padding: '6px 8px' }}>Feature</th>
                    <th style={{ padding: '6px 8px' }}>Calls</th>
                    <th style={{ padding: '6px 8px' }}>Provider cost</th>
                    <th style={{ padding: '6px 8px' }}>Charge (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {usage.rows.slice(-50).map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #F3F4F6' }}>
                      <td style={{ padding: '6px 8px' }}>{r.usage_date}</td>
                      <td style={{ padding: '6px 8px' }}>{r.provider}</td>
                      <td style={{ padding: '6px 8px' }}>{r.feature}</td>
                      <td style={{ padding: '6px 8px' }}>{r.calls}</td>
                      <td style={{ padding: '6px 8px' }}>{r.cost_currency || ''} {r.provider_cost.toFixed(4)}</td>
                      <td style={{ padding: '6px 8px' }}>{r.charge_inr != null ? r.charge_inr.toFixed(2) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div style={{ fontSize: 12, color: '#9CA3AF' }}>No usage recorded yet.</div>
        )}
      </div>

      <div className="card" style={{ padding: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>OpenAI reconciliation</div>
        {reconciliation.length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #E5E7EB', color: '#6B7280' }}>
                <th style={{ padding: '6px 8px' }}>Date</th>
                <th style={{ padding: '6px 8px' }}>Provider reported</th>
                <th style={{ padding: '6px 8px' }}>HMS metered</th>
                <th style={{ padding: '6px 8px' }}>Variance</th>
              </tr>
            </thead>
            <tbody>
              {reconciliation.map((r, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #F3F4F6' }}>
                  <td style={{ padding: '6px 8px' }}>{r.usage_date}</td>
                  <td style={{ padding: '6px 8px' }}>${r.provider_reported_cost?.toFixed(2) ?? '—'}</td>
                  <td style={{ padding: '6px 8px' }}>${r.hms_metered_cost?.toFixed(2) ?? '—'}</td>
                  <td style={{ padding: '6px 8px', color: (r.variance_pct ?? 0) > 5 ? '#991B1B' : undefined }}>{r.variance_pct != null ? `${r.variance_pct.toFixed(1)}%` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ fontSize: 12, color: '#9CA3AF' }}>No reconciliation data yet — needs a provisioned OpenAI project and an org Admin key (ai-usage-reconcile runs daily).</div>
        )}
      </div>
    </div>
  );
}
